import { asOptionalString } from "@aix/jsutils/src/coerce"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import CustomHead from "~/components/SEOHead/CustomHead"
import { api } from "~/utils/api"
import { Badge, Button, Textarea, toast } from "@aix/ui"
import { useState } from "react"
import { formatDistanceToNow } from "date-fns"

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  running: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  waiting_for_review: "bg-purple-100 text-purple-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
}

/** Inline preview for review output/reference data */
function ReviewPreview({
  label,
  data,
  displayType,
}: {
  label: string
  data: Record<string, unknown> | null
  displayType: string
}) {
  if (!data) return null
  const url = asOptionalString(data.url)
  if (!url) {
    return (
      <div className="space-y-1">
        <span className="text-muted-foreground text-xs">{label}</span>
        <pre className="bg-muted max-h-64 overflow-auto rounded p-3 text-sm">{JSON.stringify(data, null, 2)}</pre>
      </div>
    )
  }
  return (
    <div className="space-y-1">
      <span className="text-muted-foreground text-xs">{label}</span>
      {displayType === "video" ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- admin debug video preview, no captions available
        <video src={url} controls className="max-h-64 rounded" />
      ) : (
        <img src={url} alt={label} className="max-h-64 rounded" />
      )}
    </div>
  )
}

function ExecutionDetailPage() {
  const { executionId } = Route.useParams()
  const navigate = useNavigate()
  const utils = api.useUtils()

  const { data: execution, isLoading } = api.workflow.getExecution.useQuery({ executionId }, { refetchInterval: 5000 })
  const { data: reviewData } = api.pipelineReview.listReviewItems.useQuery({ executionId }, { refetchInterval: 5000 })
  const { data: generations } = api.pipelineReview.listGenerations.useQuery({ executionId })

  const { mutate: submitReview, isLoading: isSubmitting } = api.pipelineReview.submitReview.useMutation({
    onSuccess: () => {
      toast.success("Review submitted")
      void utils.pipelineReview.listReviewItems.invalidate()
      void utils.workflow.getExecution.invalidate()
    },
    onError: (err) => toast.error(err.message),
  })

  const { mutate: regenerate, isLoading: isRegenerating } = api.pipelineReview.regenerateStep.useMutation({
    onSuccess: () => {
      toast.success("Regenerating step...")
      void utils.pipelineReview.listReviewItems.invalidate()
      void utils.workflow.getExecution.invalidate()
      void utils.pipelineReview.listGenerations.invalidate()
    },
    onError: (err) => toast.error(err.message),
  })

  const [notes, setNotes] = useState("")

  if (isLoading) {
    return <div className="text-muted-foreground py-12 text-center">Loading execution details...</div>
  }

  if (!execution) {
    return <div className="py-12 text-center text-red-500">Execution not found</div>
  }

  const stepResults = (execution.stepResults as Record<string, unknown>[]) ?? []
  const reviewItems = reviewData?.items ?? []
  const pendingReview = reviewItems.find((r) => r.status === "pending")
  const rejectedItems = reviewItems.filter((r) => r.status === "rejected")

  return (
    <>
      <CustomHead title={`Pipeline Execution ${executionId.slice(0, 8)}`} />
      <div className="container mx-auto space-y-6 p-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/admin/pipelines" })}>
            &larr; Back
          </Button>
          <div>
            <h1 className="text-xl font-bold">Execution {executionId.slice(0, 12)}...</h1>
            <p className="text-muted-foreground text-sm">
              Workflow: {execution.workflowName ?? execution.workflowId} | Started{" "}
              {execution.startedAt ? formatDistanceToNow(new Date(execution.startedAt), { addSuffix: true }) : "N/A"}
            </p>
          </div>
          <Badge className={statusColors[execution.status] ?? ""}>{execution.status}</Badge>
          {(execution.status === "running" || execution.status === "waiting_for_review") && (
            <span className="text-muted-foreground animate-pulse text-xs">Auto-refreshing...</span>
          )}
        </div>

        {/* Step Timeline */}
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Step Timeline</h2>
          <div className="space-y-1">
            {stepResults.map((step, i) => {
              const s = step as { stepId: string; action: string; status: string; durationMs?: number; error?: string }
              return (
                <div key={i} className="border-border flex items-center gap-3 rounded border p-3">
                  <div className="text-muted-foreground w-6 text-center font-mono text-sm">{i + 1}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{s.stepId}</span>
                      <Badge variant="outline" className="text-xs">
                        {s.action}
                      </Badge>
                      <Badge className={statusColors[s.status] ?? ""}>{s.status}</Badge>
                    </div>
                    {s.error && <p className="mt-1 text-sm text-red-500">{s.error}</p>}
                    {s.durationMs !== undefined && s.durationMs !== null && (
                      <span className="text-muted-foreground text-xs">{(s.durationMs / 1000).toFixed(1)}s</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Review Items */}
        {reviewItems.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold">Review Items</h2>
            {reviewItems.map((item) => {
              const outputData = item.outputData as Record<string, unknown> | null
              const referenceData = item.referenceData as Record<string, unknown> | null

              return (
                <div key={item.id} className="border-border space-y-3 rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.reviewLabel}</span>
                      <Badge className={statusColors[item.status] ?? ""}>{item.status}</Badge>
                    </div>
                    <span className="text-muted-foreground text-sm">Step: {item.stepId}</span>
                  </div>

                  {/* Preview */}
                  <div className="flex gap-4">
                    <ReviewPreview label="Reference" data={referenceData} displayType={item.displayType} />
                    <ReviewPreview label="Output" data={outputData} displayType={item.displayType} />
                  </div>

                  {item.reviewNotes && <p className="text-muted-foreground text-sm">Notes: {item.reviewNotes}</p>}
                </div>
              )
            })}
          </div>
        )}

        {/* Regenerate UI for rejected items */}
        {rejectedItems.length > 0 && execution.status === "waiting_for_review" && (
          <div className="border-orange-200 bg-orange-50 space-y-3 rounded-lg border p-4">
            <h2 className="text-lg font-semibold">Rejected Items - Regenerate</h2>
            <p className="text-muted-foreground text-sm">
              These items were rejected. You can regenerate the step to get a new output.
            </p>
            {rejectedItems.map((item) => (
              <div key={item.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{item.reviewLabel}</span>
                  <Badge className={statusColors[item.status] ?? ""}>{item.status}</Badge>
                  <span className="text-muted-foreground text-sm">Step: {item.stepId}</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isRegenerating}
                  onClick={() => regenerate({ executionId, stepId: item.stepId })}
                >
                  {isRegenerating ? "Regenerating..." : "Regenerate"}
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Generation History */}
        {generations && generations.length > 0 && (
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">Generation History</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {generations.map((gen) => (
                <div key={gen.id} className="border-border space-y-2 rounded border p-3">
                  {gen.url &&
                    (gen.type === "video" ? (
                      // eslint-disable-next-line jsx-a11y/media-has-caption -- admin debug video preview, no captions available
                      <video src={gen.url} controls className="w-full rounded" />
                    ) : (
                      <img src={gen.url} alt={gen.prompt ?? "Generated"} className="w-full rounded" />
                    ))}
                  <div className="flex items-center gap-1">
                    <Badge className={statusColors[gen.status] ?? ""} variant="outline">
                      {gen.status}
                    </Badge>
                    <span className="text-muted-foreground text-xs">#{gen.generationNum}</span>
                  </div>
                  {gen.prompt && <p className="text-muted-foreground line-clamp-2 text-xs">{gen.prompt}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Review Actions */}
        {pendingReview && (
          <div className="border-primary/20 bg-primary/5 space-y-3 rounded-lg border p-4">
            <h2 className="text-lg font-semibold">Action Required: {pendingReview.reviewLabel}</h2>
            <Textarea
              placeholder="Review notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
            <div className="flex gap-2">
              <Button
                onClick={() =>
                  submitReview({ reviewItemId: pendingReview.id, status: "approved", notes: notes || undefined })
                }
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Approve"}
              </Button>
              <Button
                variant="destructive"
                onClick={() =>
                  submitReview({ reviewItemId: pendingReview.id, status: "rejected", notes: notes || undefined })
                }
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Reject"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export const Route = createFileRoute("/admin/pipelines/$executionId")({
  component: ExecutionDetailPage,
})
